import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { topUpBalanceSuccess } from "./balance.actions"
import { BALANCE_SLICE_NAME } from "./balance.constants"
import {
  firstTopUpBalance,
  getBalance,
  getBalanceActivity,
  getBalanceActivityExport,
  getBalanceActivityFilterOptions,
  setAutoTopUp,
  topUpBalance,
  updateBalance
} from "./balance.thunks"

/* ------------- Initial State ------------- */

const columns = [
  { field_id: "balance_transaction_timestamp", label: "Date", active: true },
  { field_id: "amount", label: "Amount", active: true },
  { field_id: "balance_before", label: "Balance Before", active: true },
  { field_id: "balance_after", label: "Balance After", active: true },
  { field_id: "card_last4", label: "Payment Method", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "status", label: "Status", active: true },
  { field_id: "subtype", label: "Type", active: true },
  { field_id: "source_id", label: "Source ID", active: true },
  { field_id: "source_type", label: "Service", active: true }
]

const initialState = {
  balance: {
    min_amount: 0,
    recommended: 0,
    amount: 0,
    calculated_recommended: 0,
    calculated_min_amount: 0
  },
  fetching: false,

  saveFetching: false,
  saveResult: null,

  addFundsLoading: false,

  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumns: [...columns],
  columns: [...columns],

  filtersLastUpd: null,
  filters: {
    balance_transaction_timestamp: null,

    status: "all",
    subtype: "all",
    source_type: "all",

    source_id: [],
    business_account: [],
    card_last4: [],

    amount: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    subtype: [],
    source_type: []
  },

  visibleFilters: { status: true, subtype: true, business_account: true },

  exportFetching: false,
  exportResult: null,
  adyenSessionData: null
}

// `getBalance`, `updateBalance` and `getBalanceActivity` all routed their failures through the
// legacy shared `balanceFailure` handler, which cleared both spinners — kept as-is here.
const balanceFailure = (state) => {
  state.fetching = false
  state.saveFetching = false
}

// TOP_UP_BALANCE_* and FIRST_TOP_UP_BALANCE_* shared one handler per phase in the legacy reducer.
const topUpBalancePending = (state) => {
  state.addFundsLoading = true
}
const topUpBalanceRejected = (state) => {
  state.fetching = false
  state.saveFetching = false
  state.addFundsLoading = false
}

const slice = createSlice({
  name: BALANCE_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `balanceSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `balanceSetProp({ key, value })`,
    // matching the `alertsSetProp`/`creditsSetProp` slices.
    balanceSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearBalanceFilters(state) {
      state.filters = initialState.filters
    },
    clearAdyenSessionData(state) {
      state.adyenSessionData = null
    },
    resetAddFundsLoading(state) {
      state.addFundsLoading = false
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- balance ---------------- */
      .addCase(getBalance.pending, (state) => {
        state.fetching = true
      })
      .addCase(getBalance.fulfilled, (state, action) => {
        state.fetching = false
        state.balance = action.payload
      })
      .addCase(getBalance.rejected, balanceFailure)

      /* ---------------- update balance ---------------- */
      .addCase(updateBalance.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateBalance.fulfilled, (state, action) => {
        state.saveFetching = false
        state.balance = { ...state.balance, ...action.payload }
        state.saveResult = "balance_update"
      })
      .addCase(updateBalance.rejected, balanceFailure)

      /* ---------------- auto top-up ---------------- */
      .addCase(setAutoTopUp.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(setAutoTopUp.fulfilled, (state, action) => {
        state.saveFetching = false
        state.balance = { ...state.balance, ...action.payload }
      })
      .addCase(setAutoTopUp.rejected, balanceFailure)

      /* ---------------- top up ---------------- */
      .addCase(topUpBalance.pending, topUpBalancePending)
      .addCase(firstTopUpBalance.pending, topUpBalancePending)
      // Success is handled through the plain action both thunks dispatch, not through
      // `fulfilled`, so the new state is in place before they redirect / hand the payload
      // back to the caller — see balance.actions.js.
      .addCase(topUpBalanceSuccess, (state, action) => {
        const result = action.payload
        state.saveResult = result?.message
          ? { type: "balance_top_up", message: result?.message }
          : "balance_top_up"
        state.addFundsLoading = false
        state.adyenSessionData = result?.provider === "adyen" ? result : null
      })
      .addCase(topUpBalance.rejected, topUpBalanceRejected)
      .addCase(firstTopUpBalance.rejected, topUpBalanceRejected)

      /* ---------------- activity ---------------- */
      .addCase(getBalanceActivity.pending, (state) => {
        state.fetching = true
      })
      .addCase(getBalanceActivity.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        state.result = result.results ? result : { count: result?.length || 0, results: result }
      })
      .addCase(getBalanceActivity.rejected, balanceFailure)

      /* ---------------- activity export ---------------- */
      .addCase(getBalanceActivityExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getBalanceActivityExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getBalanceActivityExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- activity filter options ---------------- */
      .addCase(getBalanceActivityFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getBalanceActivityFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getBalanceActivityFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["balance-activity"]?.data
        if (!preference || preference.version !== BALANCE_ACTIVITY_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columns || !preference.visibleFilters) return

        state.filters = preference.filters
        state.columns = preference.columns
        state.visibleFilters = preference.visibleFilters
      })
  }
})

export const balanceActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columns, visibleFilters }` payload changes.
export const BALANCE_ACTIVITY_PREFERENCES_VERSION = 2

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.balance.filters, initialState.filters)

const PERSISTED_REDUCER_VERSION = 3

export const persistedReducer = persistReducer(
  {
    key: BALANCE_SLICE_NAME,
    storage,
    whitelist: ["filters", "filtersLastUpd", "columns", "visibleFilters", "saveResult"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
